import {
	Box,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Typography,
} from "@mui/material";
import { BookLoanFormWrapper } from "./styled";
import { BaseButton } from "../../../components/button/styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { BookLoanFormPropsType, LoanType } from "../../../types/container.type";
import { formatAmountDisplay } from "../../../helpers/formatAmountDisplay";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllCustomersService } from "../../../utils/api/customerservice/customerlist/retrieveAllCustomers";
import Cookies from "universal-cookie";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import { retrieveInstitutionByIdService } from "../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCurrencyByIdService } from "../../../utils/outbound/currency/retrieveCurrencyById";
import { retrieveAllLoanProductService } from "../../../utils/api/configuration/loanproduct/retrieveAllLoanProduct";
import { retrieveSavingsAccountByCustomerIdService } from "../../../utils/outbound/savingsaccount/retrieveSavingsAccountByCustomerId";

export const BookLoanForm: React.FC<BookLoanFormPropsType> = ({
	formDetails,
	setFormDetails,
	loanProduct,
	error,
	isLoading,
	handleSubmit,
}) => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const { data: customers } = useQuery({
		queryKey: ["customers", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllCustomersService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const { data: loanProducts } = useQuery({
		queryKey: ["loanProducts", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllLoanProductService(TOKEN);
			return response?.data;
		},
		enabled: !!TOKEN,
	});

	const { data: currencies } = useQuery({
		queryKey: ["currencies", TOKEN],
		queryFn: async () => {
			const user = await retrieveUserProfileService(TOKEN);
			const institution = await retrieveInstitutionByIdService(
				user?._id,
				user?.institution,
			);
			const currencyIds = [
				...(institution?.supportingCurrencies || []),
				institution?.currency,
			].filter(Boolean);
			const currencyInfos = await Promise.all(
				currencyIds.map((currencyId) =>
					retrieveCurrencyByIdService(user?._id, currencyId)
						.then((currencyInfo) => ({
							id: currencyId,
							name: currencyInfo?.title || "Unknown Currency",
						}))
						.catch(() => ({
							id: currencyId,
							name: "Unknown Currency",
						})),
				),
			);
			const uniqueCurrencies = Array.from(
				new Map(currencyInfos.map((c) => [c.id, c])).values(),
			);
			return uniqueCurrencies;
		},
		enabled: !!TOKEN,
	});

	const fetchObligorSavingsAccount = async (obligorId: string) => {
		try {
			if (!obligorId.trim()) return;
			const response = await retrieveSavingsAccountByCustomerIdService(
				TOKEN,
				obligorId,
			);
			return setFormDetails((prev) => {
				if (!prev) return prev;
				return { ...prev, disbursementAccount: response.accountNumber };
			});
		} catch (error) {
			return console.error("Failed to fetch obligor savings account:", error);
		}
	};

	const handleChange = async (
		e:
			| SelectChangeEvent<unknown>
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const { name, type, value } = e.target as HTMLInputElement;
		if (type === "file") {
			const input = e.target as HTMLInputElement;
			const fileArray = input.files ? Array.from(input.files) : [];
			setFormDetails((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					[name]: [...(prev[name as keyof LoanType] as File[]), ...fileArray],
				};
			});
		} else {
			setFormDetails((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					[name]: value,
					...(name === "loanProduct" && {
						term: "",
						interestRate: "",
						appliedAmount: "",
					}),
					...(name === "obligor" && { disbursementAccount: "" }),
				};
			});
			if (name === "obligor") {
				await fetchObligorSavingsAccount(value);
				return;
			}
		}
		return;
	};

	return (
		<BookLoanFormWrapper onSubmit={handleSubmit}>
			<Grid2
				container
				component={"div"}
				spacing={"var(--flex-gap)"}
				className="fieldset-grid-wrapper"
			>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Obligor</BaseLabel>
						<BaseSelect
							name="obligor"
							fontweight={400}
							readOnly
							value={formDetails?.obligor}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value=" ">Select Customer</BaseOption>
							{customers?.map(
								(customer: Record<string, any>, index: number) => (
									<BaseOption key={index} value={customer.id}>
										{`${customer?.firstName} ${customer?.lastName} `
											?.split(" ")
											.map(
												(word: string) =>
													word.charAt(0).toUpperCase() + word.slice(1),
											)
											.join(" ")}
									</BaseOption>
								),
							)}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Disbursement Account Number
						</BaseLabel>
						<BaseInput
							required
							readOnly
							name="disbursementAccount"
							value={formDetails?.disbursementAccount}
							placeholder="Obligor Account Number"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Loan Product</BaseLabel>
						<BaseSelect
							name="loanProduct"
							fontweight={400}
							readOnly
							value={formDetails?.loanProduct}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value=" ">Select Loan Product</BaseOption>
							{loanProducts?.map(
								(product: Record<string, any>, index: number) => (
									<BaseOption key={index} value={product.id}>
										{product.name
											?.split(" ")
											.map(
												(word: string) =>
													word.charAt(0).toUpperCase() + word.slice(1),
											)
											.join(" ")}
									</BaseOption>
								),
							)}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Loan ID</BaseLabel>
						<BaseInput
							required
							readOnly
							name="loanId"
							value={formDetails?.loanId.toString().padStart(3, "0")}
							placeholder="Loan ID"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Loan Term</BaseLabel>
						<BaseInput
							required
							name="term"
							readOnly
							value={formDetails?.term}
							placeholder="Loan Term"
							onChange={(e) => handleChange(e)}
						/>
						{loanProduct && (
							<Box marginBlockStart={"calc(var(--basic-margin)/4)"}>
								<Typography
									variant="caption"
									fontFamily={"Inter"}
									fontWeight={"600"}
									fontSize={14}
									lineHeight={"normal"}
									color={"var(--text-salmon-pink-color)"}
									whiteSpace={"normal"}
								>
									{`Allowable term is a duration (in number of ${loanProduct?.term === "annually" ? "years" : "months"}) less than or equal to ${loanProduct?.tenor} months.`}
								</Typography>
							</Box>
						)}
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Monthly Interest Rate
						</BaseLabel>
						<BaseInput
							required
							readOnly
							name="interestRate"
							value={formDetails?.interestRate}
							placeholder="Enter Interest Rate"
							onChange={(e) => handleChange(e)}
						/>
						{loanProduct && (
							<Box marginBlockStart={"calc(var(--basic-margin)/4)"}>
								<Typography
									variant="caption"
									fontFamily={"Inter"}
									fontWeight={"600"}
									fontSize={14}
									lineHeight={"normal"}
									color={"var(--text-salmon-pink-color)"}
									whiteSpace={"normal"}
								>
									{`Allowable interest rate is a percentage interest (in number) less than or equal to ${loanProduct?.interestRate}%.`}
								</Typography>
							</Box>
						)}
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Disbursement Date</BaseLabel>
						<BaseInput
							required
							readOnly
							type="date"
							name="disbursementDate"
							value={formDetails?.disbursementDate}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							First Repayment Date
						</BaseLabel>
						<BaseInput
							required
							readOnly
							type="date"
							name="firstRepaymentDate"
							value={formDetails?.firstRepaymentDate}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, laptop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Currency</BaseLabel>
						<BaseSelect
							name="currency"
							readOnly
							fontweight={400}
							value={formDetails?.currency}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value=" ">Select Currency</BaseOption>
							{currencies?.map(
								(currency: Record<string, any>, index: number) => (
									<BaseOption key={index} value={currency.id}>
										{currency.name
											?.split(" ")
											.map(
												(word: string) =>
													word.charAt(0).toUpperCase() + word.slice(1),
											)
											.join(" ")}
									</BaseOption>
								),
							)}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, laptop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Applied Amount</BaseLabel>
						<BaseInput
							required
							readOnly
							name="appliedAmount"
							placeholder="Enter Applied Amount"
							value={formDetails?.appliedAmount}
							onChange={(e) => handleChange(e)}
						/>
						{loanProduct && (
							<Box marginBlockStart={"calc(var(--basic-margin)/4)"}>
								<Typography
									variant="caption"
									fontFamily={"Inter"}
									fontWeight={"600"}
									fontSize={14}
									lineHeight={"normal"}
									color={"var(--text-salmon-pink-color)"}
									whiteSpace={"normal"}
								>
									{`Allowable application amount is a figure (in number) less than or equal to ${formatAmountDisplay(loanProduct?.amount ?? 0)}.`}
								</Typography>
							</Box>
						)}
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, laptop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Penalty</BaseLabel>
						<BaseInput
							required
							readOnly
							name="penalty"
							placeholder="Enter Penalty"
							value={formDetails?.penalty}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Description</BaseLabel>
						<BaseInput
							required
							multiline
							readOnly
							minRows={4}
							name="description"
							value={formDetails?.description}
							placeholder="Add any additional details or notes."
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
			</Grid2>
			{error && (
				<Typography
					fontFamily={"Inter"}
					fontWeight={"600"}
					fontSize={14}
					lineHeight={"normal"}
					color={"var(--text-salmon-pink-color)"}
					whiteSpace={"normal"}
				>
					{error}
				</Typography>
			)}
			<Box overflow={"hidden"}>
				<BaseButton
					type="submit"
					variant="contained"
					bgcolor="var(--primary-color)"
					disableElevation
					disabled={isLoading}
					sx={{
						width: { mobile: "100%", miniTablet: "auto" },
					}}
				>
					{isLoading ? (
						<CircularProgress color="inherit" className="loader" />
					) : (
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Book Loan
						</Typography>
					)}
				</BaseButton>
			</Box>
		</BookLoanFormWrapper>
	);
};
